import { getOrgTypeLabel, formatDate } from './utils';
import { roleLabel } from './orgTypes';

const PLACEMENT_KIND = {
  foster_placement: 'a foster placement',
  kinship: 'a kinship placement',
  supported_lodgings: 'a supported lodgings placement',
  childrens_home: "a children's home placement",
  residential: 'a residential placement',
  semi_independent: 'a semi-independent placement',
};

/** Turn one pending_suggestions row into a plain-English title + why-line. */
export const describeSuggestion = (s, org, orgById = {}) => {
  switch (s.type) {
    case 'person':
      return {
        title: `Add ${s.name} to ${org.name}`,
        detail: [
          s.role ? `As ${roleLabel(org.type, s.role)}, under People involved` : 'Under People involved',
          s.dates_known,
        ].filter(Boolean).join(' · '),
      };
    case 'reference_numbers':
      return {
        title: `Add a reference number to ${org.name}`,
        detail: s.value,
      };
    case 'org_dates': {
      const label = s.field === 'start_date' ? 'start date' : 'end date';
      return {
        title: `Set ${org.name}'s ${label} to ${formatDate(s.date) || s.date}`,
        detail: s.label || null,
      };
    }
    case 'new_organisation': {
      const staff = (s.people || []).map((p) => p.name).filter(Boolean);
      return {
        title: `Add "${s.name}" as a new organisation`,
        detail: [getOrgTypeLabel(s.suggested_type), staff.length ? `with ${joinNames(staff)}` : null]
          .filter(Boolean).join(' · '),
      };
    }
    case 'placement': {
      const targetName = orgById[s.target_org_id]?.name;
      const kind = PLACEMENT_KIND[s.placement_type] || 'a foster placement';
      const where = targetName ? `Under ${targetName}` : 'Under “Council not yet known” until you know which council';
      return {
        title: `Add ${s.carer_name} as ${kind}`,
        detail: [
          s.address,
          where,
          s.can_be_organisation ? 'If a company or charity ran it, you can add it as its own organisation too' : null,
        ].filter(Boolean).join(' · '),
      };
    }
    case 'placement_details': {
      const bits = [
        s.address && `address ${s.address}`,
        (s.start_date || s.end_date) && 'dates',
      ].filter(Boolean);
      return {
        title: `Add the ${bits.length ? bits.join(' and ') : 'details'} to ${s.carer_name}'s placement`,
        detail: (s.start_date || s.end_date)
          ? [formatDate(s.start_date) || s.start_date, formatDate(s.end_date) || s.end_date].filter(Boolean).join(' to ')
          : null,
      };
    }
    case 'home_organisation': {
      const staff = (s.people || []).map((p) => p.name).filter(Boolean);
      return {
        title: `Add ${s.home_name} as its own organisation too`,
        detail: [
          "If it was run by a company or charity rather than the council, it holds its own records you can ask for",
          staff.length ? `with ${joinNames(staff)}` : null,
        ].filter(Boolean).join(' · '),
      };
    }
    case 'link_home': {
      const targetName = orgById[s.target_org_id]?.name || org.name;
      return {
        title: `Add ${s.home_name} as a placement under ${targetName}`,
        detail: `So your request to ${targetName} asks about your time there too`,
      };
    }
    case 'sibling':
      return {
        title: `Add ${s.name} as your brother or sister`,
        detail: 'Named in your letters to the council, so they include records about you both',
      };
    case 'respite': {
      const targetName = orgById[s.target_org_id]?.name;
      return {
        title: `Add ${s.carer_name} as a short-break (respite) carer`,
        detail: targetName ? `Under ${targetName}${s.placement_id ? ', on the placement the record falls in' : ''}` : null,
      };
    }
    case 'resolve_council':
      return {
        title: `Was ${s.name} the council that placed you?`,
        detail: `Named in your records. Confirming renames “${org.name}” to ${s.name}${org.location ? ` (${org.location})` : ''}.`,
      };
    default:
      return { title: 'A suggested update', detail: null };
  }
};

export const CONFIRM_LABEL = {
  person: 'Add',
  reference_numbers: 'Add',
  org_dates: 'Set date',
  new_organisation: 'Add organisation',
  placement: 'Add placement',
  resolve_council: 'Yes, that was the council',
  respite: 'Add short-break carer',
  placement_details: 'Add details',
  home_organisation: 'Add organisation',
  link_home: 'Add placement',
  sibling: 'Add',
};

/** Suggestion types important enough for their own row on the dashboard. */
export const STANDALONE_SUGGESTION_TYPES = new Set(['new_organisation', 'resolve_council']);

const joinNames = (names) => {
  const shown = names.slice(0, 2);
  const rest = names.length - shown.length;
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`;
  return shown.join(' and ');
};

const count = (n, singular, pluralWord) => (n === 1 ? singular : `${n} ${pluralWord}`);

/**
 * One phrase for an organisation's remaining suggestions:
 * "3 people named (Jane Smith, John Doe and 1 more), a date and a reference number".
 */
export const summariseSuggestions = (suggestions) => {
  const by = (type) => suggestions.filter((s) => s.type === type);
  const parts = [];

  const people = by('person');
  if (people.length) {
    const names = people.map((p) => p.name).filter(Boolean);
    parts.push(`${count(people.length, 'a person named', 'people named')}${names.length ? ` (${joinNames(names)})` : ''}`);
  }
  const dates = by('org_dates');
  if (dates.length) parts.push(count(dates.length, 'a date', 'dates'));
  const refs = by('reference_numbers');
  if (refs.length) parts.push(count(refs.length, 'a reference number', 'reference numbers'));
  const placements = by('placement');
  if (placements.length) parts.push(count(placements.length, 'a foster placement', 'foster placements'));
  const respite = by('respite');
  if (respite.length) parts.push(count(respite.length, 'a short-break carer', 'short-break carers'));

  const homes = [...by('home_organisation'), ...by('link_home')];
  if (homes.length) parts.push(count(homes.length, "a children's home to link up", "children's homes to link up"));
  const details = by('placement_details');
  if (details.length) parts.push(count(details.length, 'a placement address or date', 'placement details'));
  const siblings = by('sibling');
  if (siblings.length) {
    const names = siblings.map((p) => p.name).filter(Boolean);
    parts.push(`${count(siblings.length, 'a brother or sister', 'brothers and sisters')}${names.length ? ` (${joinNames(names)})` : ''}`);
  }

  const known = new Set(['person', 'org_dates', 'reference_numbers', 'placement', 'respite', 'placement_details', 'sibling',
    'home_organisation', 'link_home']);
  const other = suggestions.filter((s) => !known.has(s.type) && !STANDALONE_SUGGESTION_TYPES.has(s.type)).length;
  if (other) parts.push(count(other, 'another thing', 'other things'));

  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

// ---- Sections ----------------------------------------------------------
// Suggestions for one organisation are grouped under a heading that says
// what they are ("People you may have known at Huntington School"), not
// just the organisation's name.

const SECTION_OF = {
  person: 'people',
  placement: 'homes',
  placement_details: 'homes',
  respite: 'homes',
  link_home: 'homes',
  home_organisation: 'homes',
  sibling: 'family',
  resolve_council: 'council',
  new_organisation: 'organisations',
  org_dates: 'details',
  reference_numbers: 'details',
};
const SECTION_ORDER = ['council', 'people', 'homes', 'family', 'organisations', 'details', 'other'];
const CARE_AUTHORITY_TYPES = new Set(['council', 'fostering_agency', 'adoption']);

/** The heading for one kind of suggestion at this organisation. */
export const sectionTitle = (key, org = {}) => {
  const name = org.unresolved ? 'your council' : org.name;
  switch (key) {
    case 'people':
      if (CARE_AUTHORITY_TYPES.has(org.type)) return `People who worked with you at ${name}`;
      if (org.type === 'childrens_home') return `Staff you may have known at ${name}`;
      return `People you may have known at ${name}`;
    case 'homes':
      if (org.unresolved) return 'Places you lived';
      return CARE_AUTHORITY_TYPES.has(org.type) ? `Places you lived, arranged by ${name}` : 'Places you may have lived';
    case 'family':
      return 'Brothers and sisters named in your records';
    case 'council':
      return 'Which council was responsible for you?';
    case 'organisations':
      return `Other organisations named in ${org.unresolved ? 'your' : `${name}'s`} records`;
    case 'details':
      return `Dates and reference numbers for ${name}`;
    default:
      return `Other things found in ${org.unresolved ? 'your' : `${name}'s`} records`;
  }
};

/** [{ key, title, suggestions }] in a steady order, empty sections left out. */
export const groupSuggestionsBySection = (suggestions, org) => {
  const by = {};
  for (const s of suggestions || []) {
    const key = SECTION_OF[s.type] || 'other';
    (by[key] = by[key] || []).push(s);
  }
  return SECTION_ORDER.filter((k) => by[k]).map((key) => ({ key, title: sectionTitle(key, org), suggestions: by[key] }));
};

/** One title for a mixed set: the section's own heading when they're all
 * one kind, else what they have in common. */
export const suggestionsSummaryTitle = (suggestions, org) => {
  const sections = groupSuggestionsBySection(suggestions, org);
  if (sections.length === 1) return sections[0].title;
  return `Found in ${org.unresolved ? 'your' : `${org.name}'s`} records`;
};
